<template>
  <div class="announcement-bar" ref="barRef">
    <!-- Invisible element to set height and measure text width -->
    <span class="announcement-item invisible-measure" ref="measureRef">
      {{ props.annoucement }}
    </span>

    <div class="announcement-content">
      <span
        v-for="item in items"
        :key="item.id"
        class="announcement-item animated-item"
        :style="{ transform: `translate3d(${item.x}px, -50%, 0)` }"
      >
        {{ props.annoucement }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  annoucement: {
    type: String,
    required: true,
  },
});

const barRef = ref(null);
const measureRef = ref(null);
const items = ref([]);

let animationId;
let lastTime = 0;
let nextId = 0;
const speed = 80; // pixels per second
const spacing = 50; // spacing between texts

const loop = (time) => {
  if (!lastTime) lastTime = time;
  const deltaTime = time - lastTime;
  lastTime = time;

  const containerWidth = barRef.value?.clientWidth || 0;
  const textWidth = measureRef.value?.clientWidth || 0;

  if (containerWidth > 0 && textWidth > 0) {
    if (items.value.length === 0) {
      items.value.push({ id: nextId++, x: containerWidth });
    } else {
      const deltaX = (speed * deltaTime) / 1000;

      for (let i = 0; i < items.value.length; i++) {
        items.value[i].x -= deltaX;
      }

      const lastItem = items.value[items.value.length - 1];
      const lastItemEnd = lastItem.x + textWidth;

      if (containerWidth - lastItemEnd >= spacing) {
        items.value.push({
          id: nextId++,
          x: lastItemEnd + spacing
        });
      }

      while (items.value.length > 0 && items.value[0].x + textWidth < 0) {
        items.value.shift();
      }
    }
  }

  animationId = requestAnimationFrame(loop);
};

const startAnimation = () => {
  if (animationId) cancelAnimationFrame(animationId);
  items.value = [];
  lastTime = 0;
  animationId = requestAnimationFrame(loop);
};

onMounted(() => {
  startAnimation();
});

onBeforeUnmount(() => {
  if (animationId) cancelAnimationFrame(animationId);
});

watch(() => props.annoucement, () => {
  startAnimation();
});
</script>

<style scoped>
.announcement-bar {
  width: 100%;
  background-color: #121212e8;
  backdrop-filter: blur(4px);
  color: var(--text-light, #fbfbfa);
  overflow: hidden;
  white-space: nowrap;
  padding: 8px 0;
  position: relative;
  z-index: 100;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  border-bottom: 2px solid #3b82f6; /* Blue bottom border from the image */
}

.invisible-measure {
  display: inline-block;
  visibility: hidden;
  pointer-events: none;
}

.announcement-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* Let clicks pass through if needed */
  overflow: hidden;
}

.animated-item {
  position: absolute;
  top: 50%;
  left: 0;
  will-change: transform;
}
</style>
